import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { Navigate, NavLink } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  Chip as MuiChip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider as MuiDivider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import agent from "../../utils/axios";
import {
  MaintenanceJobInfo,
  useMaintenanceAccess,
} from "../../hooks/useMaintenanceAccess";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const Chip = styled(MuiChip)(spacing);

/** AB#6562: one tile of a job's own summary, when the job reports its own numbers. */
interface JobStat {
  label: string;
  value: number;
  highlight: boolean;
}

/** Mirrors Core.Entities.MaintenanceJobRun (camelCase over the wire). */
interface JobRun {
  id: string;
  jobName: string;
  state: "Running" | "Completed" | "Cancelled" | "Failed" | "Interrupted";
  dryRun: boolean;
  startedBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  heartbeatAt: string;
  candidatesAtStart: number;
  payeesProcessed: number;
  payeesSkipped: number;
  payeesUpdated: number;
  payeesFailed: number;
  billsAssigned: number;
  remaining: number;
  message: string | null;
  stats: JobStat[] | null;
  failures: {
    payeeId: string;
    billRefCode: string | null;
    reason: string;
    at: string;
  }[];
}

interface JobStatus {
  jobName: string;
  isRunning: boolean;
  current: JobRun | null;
  history: JobRun[];
}

/**
 * AB#6562: one row of a run's report. Every field is an id, a ref code, a date, a year or a
 * status token — nothing else from a payee reaches this page.
 */
interface CorrectionRow {
  outcome: string;
  reason: string | null;
  billRefCode: string | null;
  billId: string | null;
  payeeId: string | null;
  distributionDate: string | null;
  previousPaymentDate: string | null;
  newPaymentDate: string | null;
  previousTaxYear: number | null;
  newTaxYear: number | null;
  taxYearChanges: boolean;
}

interface CorrectionsPage {
  total: number;
  page: number;
  pageSize: number;
  rows: CorrectionRow[];
}

const POLL_MS = 3000;
/** The API caps a report page at 500, so ask for the cap and page until the run is complete. */
const CORRECTIONS_PAGE_SIZE = 500;
/**
 * A run over the whole database can write more report rows than this page should hold in
 * memory. Stop at 10,000 rows / 20 requests and say so, rather than letting a pathological run
 * hang the page — the tiles still carry the whole run's totals.
 */
const CORRECTIONS_MAX_ROWS = 10000;
const CORRECTIONS_MAX_REQUESTS = Math.ceil(
  CORRECTIONS_MAX_ROWS / CORRECTIONS_PAGE_SIZE
);
/** The row describes a correction that happened, or would happen. */
const CHANGED_OUTCOME = "Changed";
/**
 * A write that was attempted and did not land. It is listed once, in the run's own failures
 * table, so it appears in neither report table.
 */
const FAILED_OUTCOME = "Failed";

const stateColor = (
  state: JobRun["state"]
): "default" | "primary" | "success" | "warning" | "error" => {
  switch (state) {
    case "Running":
      return "primary";
    case "Completed":
      return "success";
    case "Cancelled":
    case "Interrupted":
      return "warning";
    case "Failed":
      return "error";
    default:
      return "default";
  }
};

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString("en-US");
const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : "—";
const elapsed = (run: JobRun) => {
  const start = new Date(run.startedAt).getTime();
  const end = run.finishedAt
    ? new Date(run.finishedAt).getTime()
    : new Date(run.heartbeatAt).getTime();
  const s = Math.max(0, Math.round((end - start) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

/**
 * The response interceptor rejects with `error.response` itself, not with an AxiosError, so
 * `e.response` is undefined here and reading only that collapses every API message to the
 * fallback. Try both shapes, the way BillDetails does.
 */
const apiMessage = (e: any, fallback: string): string =>
  e?.response?.data?.message ?? e?.data?.message ?? e?.message ?? fallback;

/**
 * One CSV cell. Excel and Sheets execute a cell whose text starts with `=`, `+`, `-`, `@`, a tab
 * or a carriage return as a formula. Every value here is server-generated today, but the report
 * is a file Finance opens, so every field is defended the same way: prefix those with an
 * apostrophe, which Excel strips on display, then quote and double the quotes.
 */
const csvField = (value: string | number | null | undefined): string => {
  const text = value === null || value === undefined ? "" : String(value);
  const safe = /^[-=+@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

const Stat = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) => (
  <Grid item xs={6} sm={4} md={2}>
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        textAlign: "center",
        ...(highlight ? { borderColor: "warning.main" } : {}),
      }}
    >
      <Typography variant="h4" color={highlight ? "warning.main" : undefined}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  </Grid>
);

/**
 * Open/closed state for one collapsible part of the page, remembered per browser so an operator
 * who closes "Previous runs" once does not have to close it again. Storage can be blocked
 * (private window, cleared site data), so every read and write falls back to the default.
 */
const OPEN_STATE_PREFIX = "maintenance.open.";

function usePersistentOpen(
  key: string,
  defaultOpen: boolean
): [boolean, () => void] {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const saved = window.localStorage.getItem(OPEN_STATE_PREFIX + key);
      return saved === null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(OPEN_STATE_PREFIX + key, next ? "1" : "0");
    } catch {
      // Storage is unavailable; the choice lasts until the page is reloaded.
    }
  };

  return [open, toggle];
}

const ExpandToggle = ({
  open,
  label,
  onClick,
}: {
  open: boolean;
  label: string;
  onClick: (e: React.MouseEvent) => void;
}) => (
  <IconButton
    size="small"
    onClick={onClick}
    aria-expanded={open}
    aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
  >
    <ExpandMoreIcon
      fontSize="small"
      sx={{
        transform: open ? "rotate(180deg)" : "none",
        transition: "transform 150ms",
      }}
    />
  </IconButton>
);

/** A titled block of a job card whose body can be collapsed from its header. */
function Section({
  storageKey,
  label,
  title,
  defaultOpen = true,
  actions,
  mt = 4,
  children,
}: {
  storageKey: string;
  label: string;
  title: React.ReactNode;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
  mt?: number;
  children: React.ReactNode;
}) {
  const [open, toggle] = usePersistentOpen(storageKey, defaultOpen);

  return (
    <Box mt={mt}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          onClick={toggle}
          sx={{ cursor: "pointer", userSelect: "none" }}
        >
          <ExpandToggle
            open={open}
            label={label}
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
          />
          <Typography variant="subtitle2">{title}</Typography>
        </Box>
        {actions}
      </Box>
      <Collapse in={open}>
        <Box mt={1}>{children}</Box>
      </Collapse>
    </Box>
  );
}

function JobCard({ job }: { job: MaintenanceJobInfo }) {
  const name = job.name;
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [corrections, setCorrections] = useState<CorrectionsPage | null>(null);
  const [correctionsError, setCorrectionsError] = useState<string | null>(null);
  const [loadingCorrections, setLoadingCorrections] = useState(false);
  /** Bumped by the Retry button so a transient report failure does not need a new run. */
  const [reportAttempt, setReportAttempt] = useState(0);
  const [changedPage, setChangedPage] = useState(0);
  const [changedRowsPerPage, setChangedRowsPerPage] = useState(25);
  const [skippedPage, setSkippedPage] = useState(0);
  const [skippedRowsPerPage, setSkippedRowsPerPage] = useState(25);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s: JobStatus = await agent.Maintenance.status(name);
      setStatus(s);
      setError(null);
    } catch (e: any) {
      setError(apiMessage(e, "Could not load job status."));
    }
  }, [name]);

  // Poll while a run is active; otherwise just load once.
  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (status?.isRunning) {
      if (!timer.current) {
        timer.current = setInterval(refresh, POLL_MS);
      }
    } else if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [status?.isRunning, refresh]);

  const run = status?.current ?? null;
  const running = status?.isRunning ?? false;
  const runId = run?.id ?? null;

  // The report is the run's own output, so it is fetched once the run has stopped moving.
  useEffect(() => {
    if (!job.hasReport || !runId) {
      return;
    }
    if (running) {
      // A new run replaces the report; do not show the previous run's rows beside it.
      setCorrections(null);
      setCorrectionsError(null);
      return;
    }
    let alive = true;
    setLoadingCorrections(true);
    setChangedPage(0);
    setSkippedPage(0);

    /**
     * The report is the dry run's per-bill 1099 preview and the apply's audit, so a partial view
     * defeats it: page through the whole run rather than showing the first 500 rows beside tiles
     * that count all of them. Sequentially — one page at a time, so a big run does not open 20
     * requests at once — and never past the ceiling, which the caller then reports.
     */
    const loadAll = async (): Promise<CorrectionsPage> => {
      const rows: CorrectionRow[] = [];
      let total = 0;
      for (let page = 1; page <= CORRECTIONS_MAX_REQUESTS; page += 1) {
        const r: CorrectionsPage = await agent.Maintenance.corrections(
          name,
          runId,
          page,
          CORRECTIONS_PAGE_SIZE
        );
        if (!alive) {
          break;
        }
        // `total` is the whole run's count, not the page's; trust the latest response for it.
        total = r.total ?? 0;
        const batch = r.rows ?? [];
        rows.push(...batch);
        if (batch.length === 0 || rows.length >= total) {
          break;
        }
      }
      return {
        total,
        page: 1,
        pageSize: CORRECTIONS_PAGE_SIZE,
        rows: rows.slice(0, CORRECTIONS_MAX_ROWS),
      };
    };

    loadAll()
      .then((r: CorrectionsPage) => {
        if (alive) {
          setCorrections(r);
          setCorrectionsError(null);
        }
      })
      .catch((e: any) => {
        if (alive) {
          setCorrections(null);
          setCorrectionsError(apiMessage(e, "Could not load the run report."));
        }
      })
      .finally(() => {
        if (alive) {
          setLoadingCorrections(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [job.hasReport, name, runId, running, reportAttempt]);

  const start = async (dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await agent.Maintenance.start(name, dryRun);
      await refresh();
    } catch (e: any) {
      const message = apiMessage(e, "Could not start the job.");
      // Refresh on failure too: losing the start race to another operator is exactly when
      // this card is showing a stale idle state. refresh() clears the banner, so set it after.
      await refresh();
      setError(message);
    } finally {
      setBusy(false);
      setConfirmApply(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await agent.Maintenance.cancel(name);
      await refresh();
    } catch (e: any) {
      setError(apiMessage(e, "Could not cancel the job."));
    } finally {
      setBusy(false);
    }
  };

  const allRows = corrections?.rows ?? [];
  const correctionsTotal = corrections?.total ?? 0;
  /** The ceiling (or a short page) stopped the walk before the run's last row. */
  const correctionsTruncated =
    !!corrections && allRows.length < correctionsTotal;
  // AB#5373 rework: the bad-TIN cleanup records a payee deletion as a "Changed" row naming no bill
  // (no id, no ref code). Those get their own table so a deletion is never shown as a corrected bill.
  const isPayeeRow = (r: CorrectionRow) => !r.billId && !r.billRefCode;
  const changedOrDeleted = allRows.filter((r) => r.outcome === CHANGED_OUTCOME);
  const changedRows = changedOrDeleted.filter((r) => !isPayeeRow(r));
  const deletedPayeeRows = changedOrDeleted.filter(isPayeeRow);
  // AB#6339: a job whose change is not a date (the bill reassignment) says what it does in the
  // reason. Shown only when some changed row carries one (the date job's 1099-year note does too).
  const changedHaveDetail = changedRows.some((r) => !!r.reason);
  // Failed is out of both tables: the failures table above already lists it.
  const failedRows = allRows.filter((r) => r.outcome === FAILED_OUTCOME);
  const skippedRows = allRows.filter(
    (r) => r.outcome !== CHANGED_OUTCOME && r.outcome !== FAILED_OUTCOME
  );
  // A kept payee (AB#5373) is a payee row, not a bill, so the table is not only "bills" then.
  const skippedHavePayeeRows = skippedRows.some(isPayeeRow);

  const downloadReport = () => {
    if (allRows.length === 0) {
      return;
    }
    // Finance keeps this file, so a short one has to say it is short on the first line — the tiles
    // it will be reconciled against count the whole run.
    const truncationNotice = correctionsTruncated
      ? csvField(
          `PARTIAL REPORT — this file holds ${allRows.length} of the run's ${correctionsTotal} rows. ` +
            `The remaining rows were not loaded by the Maintenance page.`
        )
      : null;
    const headers = [
      "Outcome",
      "Reason",
      "Bill Ref Code",
      "Bill Id",
      "Payee Id",
      "Distribution Date",
      "Previous Payment Date",
      "New Payment Date",
      "Previous Tax Year",
      "New Tax Year",
      "Tax Year Changes",
    ]
      .map(csvField)
      .join(",");
    const rows = allRows.map((r) =>
      [
        r.outcome,
        r.reason ?? "",
        r.billRefCode ?? "",
        r.billId ?? "",
        r.payeeId ?? "",
        r.distributionDate ? new Date(r.distributionDate).toLocaleString() : "",
        r.previousPaymentDate
          ? new Date(r.previousPaymentDate).toLocaleString()
          : "",
        r.newPaymentDate ? new Date(r.newPaymentDate).toLocaleString() : "",
        r.previousTaxYear ?? "",
        r.newTaxYear ?? "",
        r.taxYearChanges ? "Yes" : "No",
      ]
        .map(csvField)
        .join(",")
    );
    const csvContent = (
      truncationNotice
        ? [truncationNotice, headers, ...rows]
        : [headers, ...rows]
    ).join("\n");

    // Create a Blob and trigger the download
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${name}-${runId ?? "run"}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const progress =
    run && run.candidatesAtStart > 0
      ? Math.min(100, (run.payeesProcessed / run.candidatesAtStart) * 100)
      : 0;
  const unit = job.unitNoun || "payees";
  // Collapsing a card hides its figures, report and history. The buttons, alerts and a running
  // job's progress stay visible, so nothing that needs attention can be folded away.
  // Cards start collapsed, so the page opens as a short list of jobs; a card the operator opens
  // stays open (remembered per browser) until they close it.
  const [cardOpen, toggleCard] = usePersistentOpen(`${job.name}.card`, false);

  return (
    <Card mb={6}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              onClick={toggleCard}
              sx={{ cursor: "pointer", userSelect: "none" }}
            >
              <ExpandToggle
                open={cardOpen}
                label={job.title}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCard();
                }}
              />
              <Typography variant="h6">{job.title}</Typography>
            </Box>
            {run && (
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={run.state}
                  color={stateColor(run.state)}
                />
                {run.dryRun && (
                  <Chip size="small" variant="outlined" label="dry run" />
                )}
                <Typography variant="body2" color="text.secondary">
                  started {when(run.startedAt)} by {run.startedBy ?? "—"} ·{" "}
                  {elapsed(run)}
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              disabled={busy || running}
              onClick={() => start(true)}
            >
              Dry run
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={busy || running}
              onClick={() => setConfirmApply(true)}
            >
              Run and apply
            </Button>
            <Button
              variant="outlined"
              color="warning"
              disabled={busy || !running}
              onClick={cancel}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {run?.state === "Interrupted" && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {run.message}
          </Alert>
        )}

        {running && (
          <Box mt={4}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              {fmt(run?.payeesProcessed)} of {fmt(run?.candidatesAtStart)}{" "}
              {unit} processed ({Math.round(progress)}%) · refreshes every 3s
            </Typography>
          </Box>
        )}

        <Collapse in={cardOpen}>
          {run && (
            <Grid container spacing={3} mt={2}>
              {run.stats && run.stats.length > 0 ? (
                run.stats.map((s) => (
                  <Stat
                    key={s.label}
                    label={s.label}
                    value={fmt(s.value)}
                    highlight={s.highlight}
                  />
                ))
              ) : (
                <React.Fragment>
                  <Stat label="Candidates" value={fmt(run.candidatesAtStart)} />
                  <Stat label="Processed" value={fmt(run.payeesProcessed)} />
                  <Stat
                    label={run.dryRun ? "Codes needed" : "Codes assigned"}
                    value={fmt(run.billsAssigned)}
                  />
                  <Stat label="Payees updated" value={fmt(run.payeesUpdated)} />
                  <Stat label="Remaining" value={fmt(run.remaining)} />
                  <Stat
                    label={`Failed${
                      run.payeesSkipped
                        ? ` / skipped ${fmt(run.payeesSkipped)}`
                        : ""
                    }`}
                    value={fmt(run.payeesFailed)}
                  />
                </React.Fragment>
              )}
            </Grid>
          )}

          {run?.message && run.state !== "Interrupted" && (
            <Typography variant="body2" mt={3}>
              {run.message}
            </Typography>
          )}

          {run && run.failures?.length > 0 && (
            <Section
              storageKey={`${job.name}.failures`}
              label="errored bills"
              title={
                <>
                  Errored bills ({fmt(run.failures.length)}
                  {run.failures.length >= 500 ? ", first 500 shown" : ""})
                </>
              }
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Payee</TableCell>
                    <TableCell>Bill Ref Code</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {run.failures.map((f, i) => (
                    <TableRow key={`${f.payeeId}-${f.billRefCode}-${i}`}>
                      <TableCell>
                        <Link
                          component={NavLink}
                          to={`/payees/detail/${f.payeeId}`}
                        >
                          {f.payeeId}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {f.billRefCode ? (
                          <Link
                            component={NavLink}
                            to={`/bills/detail/${f.billRefCode}`}
                          >
                            {f.billRefCode}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{f.reason}</TableCell>
                      <TableCell>{when(f.at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {job.hasReport && run && !running && (
            <Section
              storageKey={`${job.name}.report`}
              label="run report"
              title={
                <>
                  Run report{" "}
                  {loadingCorrections
                    ? "— loading…"
                    : `— showing ${fmt(allRows.length)} of ${fmt(
                        correctionsTotal
                      )} rows`}
                </>
              }
              actions={
                <Box display="flex" gap={2}>
                  {correctionsError && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={loadingCorrections}
                      onClick={() => setReportAttempt((n) => n + 1)}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={loadingCorrections || allRows.length === 0}
                    onClick={downloadReport}
                  >
                    Download report
                  </Button>
                </Box>
              }
            >
              {loadingCorrections && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <CircularProgress size={20} />
                </Box>
              )}

              {correctionsError && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {correctionsError}
                </Alert>
              )}

              {correctionsTruncated && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  Only {fmt(allRows.length)} of this run&apos;s{" "}
                  {fmt(correctionsTotal)} report rows were loaded — this page
                  stops at {fmt(CORRECTIONS_MAX_ROWS)}. The tables below and the
                  downloaded report cover those {fmt(allRows.length)} rows only,
                  while the tiles above count the whole run, so the two will not
                  add up. The downloaded file says so on its first line.
                </Alert>
              )}

              {failedRows.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={3}
                >
                  {fmt(failedRows.length)} bills whose write failed are listed
                  under Errored bills above — they are in neither table below.
                </Typography>
              )}

              {changedRows.length > 0 && (
                <Section
                  storageKey={`${job.name}.changed`}
                  label="changed bills"
                  mt={3}
                  title={`${
                    run.dryRun ? "Bills that would change" : "Bills corrected"
                  } (${fmt(changedRows.length)})`}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bill Ref Code</TableCell>
                        <TableCell>Distribution date</TableCell>
                        <TableCell>Previous payment date</TableCell>
                        <TableCell>New payment date</TableCell>
                        <TableCell>Tax year</TableCell>
                        {changedHaveDetail && <TableCell>Detail</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {changedRows
                        .slice(
                          changedPage * changedRowsPerPage,
                          changedPage * changedRowsPerPage + changedRowsPerPage
                        )
                        .map((r) => (
                          <TableRow key={`${r.payeeId}-${r.billId}`}>
                            <TableCell>
                              {r.billRefCode ? (
                                <Link
                                  component={NavLink}
                                  to={`/bills/detail/${r.billRefCode}`}
                                >
                                  {r.billRefCode}
                                </Link>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell>{when(r.distributionDate)}</TableCell>
                            <TableCell>{when(r.previousPaymentDate)}</TableCell>
                            <TableCell>{when(r.newPaymentDate)}</TableCell>
                            <TableCell>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                              >
                                <span>
                                  {r.previousTaxYear ?? "—"} →{" "}
                                  {r.newTaxYear ?? "—"}
                                </span>
                                {r.taxYearChanges && (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    label="tax year changes"
                                  />
                                )}
                              </Box>
                            </TableCell>
                            {changedHaveDetail && (
                              <TableCell>{r.reason ?? "—"}</TableCell>
                            )}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={changedRows.length}
                    rowsPerPage={changedRowsPerPage}
                    page={changedPage}
                    onPageChange={(_e, p) => setChangedPage(p)}
                    onRowsPerPageChange={(e) => {
                      setChangedRowsPerPage(parseInt(e.target.value, 10));
                      setChangedPage(0);
                    }}
                  />
                </Section>
              )}

              {deletedPayeeRows.length > 0 && (
                <Section
                  storageKey={`${job.name}.deleted`}
                  label="deleted payees"
                  mt={3}
                  title={`${
                    run.dryRun
                      ? "Payees that would be deleted"
                      : "Payees deleted"
                  } (${fmt(deletedPayeeRows.length)})`}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Payee</TableCell>
                        <TableCell>Detail</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deletedPayeeRows.map((r, i) => (
                        <TableRow key={`${r.payeeId}-${i}`}>
                          {/* Not a link: a deleted payee has no detail page left to open. */}
                          <TableCell>{r.payeeId ?? "—"}</TableCell>
                          <TableCell>{r.reason ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Section>
              )}

              {skippedRows.length > 0 && (
                <Section
                  storageKey={`${job.name}.skipped`}
                  label={
                    skippedHavePayeeRows
                      ? "left for review"
                      : "bills left for review"
                  }
                  mt={3}
                  title={`${
                    skippedHavePayeeRows
                      ? "Left for review"
                      : "Bills left for review"
                  } (${fmt(skippedRows.length)})`}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Payee</TableCell>
                        <TableCell>Bill Ref Code</TableCell>
                        <TableCell>Previous payment date</TableCell>
                        <TableCell>Outcome</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {skippedRows
                        .slice(
                          skippedPage * skippedRowsPerPage,
                          skippedPage * skippedRowsPerPage + skippedRowsPerPage
                        )
                        .map((r, i) => (
                          <TableRow key={`${r.payeeId}-${r.billId}-${i}`}>
                            <TableCell>
                              {r.payeeId ? (
                                <Link
                                  component={NavLink}
                                  to={`/payees/detail/${r.payeeId}`}
                                >
                                  {r.payeeId}
                                </Link>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell>
                              {r.billRefCode ? (
                                <Link
                                  component={NavLink}
                                  to={`/bills/detail/${r.billRefCode}`}
                                >
                                  {r.billRefCode}
                                </Link>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell>{when(r.previousPaymentDate)}</TableCell>
                            <TableCell>{r.outcome}</TableCell>
                            <TableCell>{r.reason ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={skippedRows.length}
                    rowsPerPage={skippedRowsPerPage}
                    page={skippedPage}
                    onPageChange={(_e, p) => setSkippedPage(p)}
                    onRowsPerPageChange={(e) => {
                      setSkippedRowsPerPage(parseInt(e.target.value, 10));
                      setSkippedPage(0);
                    }}
                  />
                </Section>
              )}

              {!loadingCorrections &&
                !correctionsError &&
                allRows.length === 0 && (
                  <Typography variant="body2" color="text.secondary" mt={3}>
                    This run produced no report rows: nothing needed correcting.
                  </Typography>
                )}
            </Section>
          )}

          {status && status.history.length > 0 && (
            <Section
              storageKey={`${job.name}.history`}
              label="previous runs"
              defaultOpen={false}
              title={`Previous runs (${fmt(status.history.length)})`}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Finished</TableCell>
                    <TableCell align="right">Bills</TableCell>
                    <TableCell align="right">Updated</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {status.history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>
                        <Chip
                          size="small"
                          label={h.state}
                          color={stateColor(h.state)}
                        />
                      </TableCell>
                      <TableCell>{h.dryRun ? "dry run" : "apply"}</TableCell>
                      <TableCell>{when(h.startedAt)}</TableCell>
                      <TableCell>{when(h.finishedAt)}</TableCell>
                      <TableCell align="right">
                        {fmt(h.billsAssigned)}
                      </TableCell>
                      <TableCell align="right">
                        {fmt(h.payeesUpdated)}
                      </TableCell>
                      <TableCell align="right">{fmt(h.payeesFailed)}</TableCell>
                      <TableCell>{h.startedBy ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          {!run && status && (
            <Typography variant="body2" color="text.secondary" mt={3}>
              This job has not been run in this environment yet.
            </Typography>
          )}
        </Collapse>
      </CardContent>

      <Dialog open={confirmApply} onClose={() => setConfirmApply(false)}>
        <DialogTitle>Run and apply?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {job.applyConfirmation ||
              "This writes to the database in this environment. It runs in the background and can be cancelled at a page boundary; it is safe to run again. Run a dry run first if you have not."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApply(false)}>Back</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={() => start(false)}
          >
            Yes, apply
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function Maintenance() {
  const { loading, canOperate, jobs, user } = useMaintenanceAccess();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canOperate) {
    return <Navigate to="/" replace />;
  }

  return (
    <React.Fragment>
      <Helmet title="Maintenance" />
      <Typography variant="h3" gutterBottom display="inline">
        Maintenance
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Dashboard
        </Link>
        <Typography>Maintenance</Typography>
      </Breadcrumbs>
      <Typography variant="body2" color="text.secondary" mt={2}>
        One-off data jobs that run inside the API. Signed in as {user}. Progress
        is saved after every page, so a job interrupted by a restart is resumed
        by starting it again.
      </Typography>
      <Divider my={6} />
      {jobs.map((j) => (
        <JobCard key={j.name} job={j} />
      ))}
    </React.Fragment>
  );
}

export default Maintenance;
